import { Button, Grid, styled, ToggleButton, ToggleButtonGroup, Accordion, AccordionSummary, AccordionDetails, IconButton, Switch, Box } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { shiftApi } from "api";
import { ShiftAvailabilityRequestEntity } from "api/generated";
import AppModal from "components/AppModal";
import { H2, H4 } from "components/Typography";
import CalendarInput from "components/input-fields/CalendarInput";
import { addDays } from "date-fns";
import { useFormik } from "formik";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEndDateValid, isValidDate } from "utils/dateValidator";
import * as Yup from 'yup';
import { useSeason } from "contexts/SeasonContext";

interface ModalProps {
    open: boolean;
    department: { id: number, label: string };
    onClose: () => void;
    onSubmit: (startDate: Date, endDate: Date, mode: string, expirationDate: Date) => void;
}

const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 700,
    minWidth: 300,
    [theme.breakpoints.down(325)]: { maxWidth: '100%' }
}));

const ModalContentWrapper = styled('div')(({ theme }) => ({
    maxHeight: '80vh',
    overflowY: 'auto',
    padding: theme.spacing(2)
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
    borderRadius: theme.shape.borderRadius,
    '&:before': {
        display: 'none'
    }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    minHeight: '48px',
    '& .MuiAccordionSummary-content': {
        margin: theme.spacing(1, 0),
        display: 'flex',
        alignItems: 'center'
    }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default
}));

const StatusDot = styled('span')<{ color: string }>(({ color, theme }) => ({
    display: 'inline-block',
    width: 10,
    height: 10,
    marginRight:5,
    borderRadius: '50%',
    backgroundColor: color,
    marginLeft: theme.spacing(1),
    verticalAlign: 'middle'
}));

const RequestAviability: FC<ModalProps> = ({ open, onClose, onSubmit, department }) => {
    const { t } = useTranslation();
    const { seasonId } = useSeason();
    const [mode, setMode] = useState('department');
    const [existingRequests, setExistingRequests] = useState<ShiftAvailabilityRequestEntity[]>([]);
    const [editModes, setEditModes] = useState<Record<number, boolean>>({});
    const [editedRequests, setEditedRequests] = useState<Record<number, ShiftAvailabilityRequestEntity>>({});

    const fieldValidationSchema = Yup.object().shape({
        startDate: Yup.date()
            .typeError(t('employees.agreements.validation.typeError'))
            .required(t('common.forms.field.required', {
                field: t('employees.agreements.field.date'),
            }))
            .test('is-valid-date', t('common.forms.validation.invalidDate'), (value) => isValidDate(value)),
        expirationDate: Yup.date()
            .typeError(t('employees.agreements.validation.typeError'))
            .required(t('common.forms.field.required', {
                field: t('employees.agreements.field.expirationDate'),
            }))
            .test('is-valid-date', t('common.forms.validation.invalidDate'), (value) => isValidDate(value)),
        endDate: Yup.date()
            .typeError(t('employees.agreements.validation.typeError'))
            .required(t('common.forms.field.required', {
                field: t('employees.agreements.field.date'),
            }))
            .test('isValidDate', t('common.forms.validation.invalidDate'), (value) => isValidDate(value))
            .test('isEndDateValid', t('employees.agreements.validation.endDateTime'), function (value) {
                return isEndDateValid(value, this.parent.startDate);
            }),
    });

    const initialValues = {
        startDate: addDays(new Date(), 7),
        endDate: addDays(new Date(), 14),
        expirationDate: addDays(new Date(), 6),
    };

    const { values, errors, handleSubmit, setFieldValue, setValues } = useFormik({
        initialValues,
        validationSchema: fieldValidationSchema,
        onSubmit: (vals) => {
            onSubmit(vals.startDate, vals.endDate, mode, vals.expirationDate);
            onClose();
        }
    });

    useEffect(() => {
        setValues(initialValues);
        setMode('department');
    }, [open, setValues]);

    useEffect(() => {
        const fetchExistingRequests = async () => {
            // Always use the currently selected seasonId from context
            // Pass departmentId and seasonId as query parameters
            // Don't pass departmentId if it's 0 (invalid department)
            const requests = (await shiftApi.allAvailabilityRequests(
                department.id && department.id !== 0 ? department.id : undefined,
                seasonId
            )).data;
            setExistingRequests(requests);
        };
        fetchExistingRequests();
    }, [values, seasonId, department.id]);

    const formatItalianDate = (dateStr?: string | null) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('it-IT');
    };

    const isRequestExpired = (req: ShiftAvailabilityRequestEntity) => {
        if (!req.expiration) return false;
        return new Date(req.expiration) < new Date();
    };

    const isRequestOpen = (req: ShiftAvailabilityRequestEntity) => {
        if (isRequestExpired(req)) return false;
        return !req.closed;
    };

    const handleEditClick = (request: ShiftAvailabilityRequestEntity) => {
        setEditModes((prev) => ({
            ...prev,
            [request.id]: !prev[request.id]
        }));
        setEditedRequests((prev) => ({
            ...prev,
            [request.id]: { ...request }
        }));
    };

    const handleChange = (reqId: number, field: 'startDate' | 'endDate' | 'expiration' | 'closed', value: any) => {
        setEditedRequests((prev) => ({
            ...prev,
            [reqId]: {
                ...prev[reqId],
                [field]: value
            }
        }));
    };

    const handleSave = async (req: ShiftAvailabilityRequestEntity) => {
        const { id, createdBy, department, ...data } = req;
        shiftApi.updateAvailability(id, data);
        setEditModes((prev) => ({ ...prev, [req.id]: false }));
        setExistingRequests((prev) =>
            prev.map((item) => (item.id === req.id ? { ...req } : item))
        );
    };

    return (
        <StyledAppModal open={open} handleClose={onClose}>
            <ModalContentWrapper>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <H2>{t('shift.selectDate')}</H2>
                        </Grid>
                        <Grid item xs={12}>
                            {t('shift.request') + ': '}
                            <ToggleButtonGroup
                                color="primary"
                                sx={{ marginLeft: 2 }}
                                value={mode}
                                exclusive
                                onChange={(e, value) => { setMode(value) }}
                            >
                                <ToggleButton value='department'>
                                    {t('department.itemName') + ': ' + department.label}
                                </ToggleButton>
                                <ToggleButton value='all'>
                                    {t('common.all')}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                        <Grid item xs={6}>
                            <CalendarInput
                                format='dd.MM.yyyy'
                                label={t('season.startDate')}
                                value={values.startDate}
                                onChange={(value) => {
                                    if (value && !isNaN(+value)) setFieldValue('startDate', value);
                                }}
                                slotProps={{
                                    textField: {
                                        error: !!errors.startDate,
                                        helperText: errors.startDate as any ?? ''
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <CalendarInput
                                format='dd.MM.yyyy'
                                label={t('season.endDate')}
                                value={values.endDate}
                                onChange={(value) => {
                                    if (value && !isNaN(+value)) setFieldValue('endDate', value);
                                }}
                                slotProps={{
                                    textField: {
                                        error: !!errors.endDate,
                                        helperText: errors.endDate as any ?? ''
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <H4>{t("employees.agreements.field.expirationDate")}</H4>
                        </Grid>
                        <Grid item xs={6}>
                            <CalendarInput
                                format='dd.MM.yyyy'
                                label={t('employees.agreements.field.expirationDate')}
                                value={values.expirationDate}
                                onChange={(value) => {
                                    if (value && !isNaN(+value)) setFieldValue('expirationDate', value);
                                }}
                                slotProps={{
                                    textField: {
                                        error: !!errors.expirationDate,
                                        helperText: errors.expirationDate as any ?? ''
                                    }
                                }}
                            />
                        </Grid>
                        {existingRequests.length > 0 && (
                            <Grid item xs={12}>
                                <StyledAccordion defaultExpanded={false}>
                                    <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <H4 padding={1}>{t('shift.existingRequests')}</H4>
                                    </StyledAccordionSummary>
                                    <StyledAccordionDetails>
                                        {existingRequests.map((req) => {
                                            const startInPast = new Date(req.startDate) < new Date();
                                            const openStatus = isRequestOpen(req);
                                            const expiredStatus = isRequestExpired(req);
                                            const editMode = !!editModes[req.id];
                                            const edited = editedRequests[req.id];
                                            return (
                                                <StyledAccordion key={req.id} sx={{ marginTop: 1 }}>
                                                    <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                        <Box
                                                            padding={1}
                                                            fontStyle={startInPast ? 'italic' : 'normal'}
                                                            color={startInPast ? 'grey' : 'inherit'}
                                                            fontWeight={600}
                                                            display='flex'
                                                            alignItems='center'
                                                        >
                                                            {formatItalianDate(req.startDate)} - {formatItalianDate(req.endDate)}
                                                            {req.department && ` (${req.department.name})`}
                                                            <StatusDot color={expiredStatus || !openStatus ? 'red' : 'green'} />
                                                        </Box>
                                                    </StyledAccordionSummary>
                                                    <StyledAccordionDetails>
                                                        <Grid container spacing={2}>
                                                            {!editMode && (
                                                                <>
                                                                    <Grid item xs={6}>
                                                                        <Box fontWeight={600}>
                                                                            {t('employees.calendar.entry.createdBy')}:
                                                                        </Box>
                                                                        <Box>
                                                                            {req.createdBy
                                                                                ? `${req.createdBy.firstName} ${req.createdBy.lastName}`
                                                                                : ''}
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6}>
                                                                        <Box fontWeight={600}>
                                                                            {t('employees.calendar.entry.createdAt')}:
                                                                        </Box>
                                                                        <Box>
                                                                            {formatItalianDate(req.createdAt)}
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6}>
                                                                        <Box fontWeight={600}>
                                                                            {t('employees.agreements.field.expirationDate')}:
                                                                        </Box>
                                                                        <Box>
                                                                            {req.expiration ? formatItalianDate(req.expiration) : '-'}
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={5}>
                                                                        <Box fontWeight={600}>
                                                                            {t('status.label')}:
                                                                        </Box>
                                                                        {expiredStatus ? (
                                                                            <Box display="inline-flex" alignItems="center">
                                                                                <StatusDot color="red" />
                                                                                {" " + t('common.closed')}
                                                                            </Box>
                                                                        ) : openStatus ? (
                                                                            <Box display="inline-flex" alignItems="center">
                                                                                <StatusDot color="green" />
                                                                                {" " + t('common.open')}
                                                                            </Box>
                                                                        ) : (
                                                                            <Box display="inline-flex" alignItems="center">
                                                                                <StatusDot color="red" />
                                                                                {" " + t('common.closed')}
                                                                            </Box>
                                                                        )}
                                                                    </Grid>
                                                                    <Grid item xs={1} textAlign="right">
                                                                        <IconButton onClick={() => { handleEditClick(req) }}>
                                                                            <EditIcon />
                                                                        </IconButton>
                                                                    </Grid>
                                                                </>
                                                            )}
                                                            {editMode && edited && (
                                                                <>
                                                                    <Grid item xs={6}>
                                                                        <Box fontWeight={600}>
                                                                            {t('employees.calendar.entry.createdBy')}:
                                                                        </Box>
                                                                        <Box>
                                                                            {req.createdBy
                                                                                ? `${req.createdBy.firstName} ${req.createdBy.lastName}`
                                                                                : ''}
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6}>
                                                                        <Box fontWeight={600}>
                                                                            {t('employees.calendar.entry.createdAt')}:
                                                                        </Box>
                                                                        <Box>
                                                                            {formatItalianDate(req.createdAt)}
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <Box fontWeight={600}>{t('season.startDate')}:</Box>
                                                                        <CalendarInput
                                                                            format='dd.MM.yyyy'
                                                                            value={new Date(edited.startDate)}
                                                                            onChange={(date) => {
                                                                                if (date && !isNaN(+date)) {
                                                                                    handleChange(req.id, 'startDate', date.toISOString());
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <Box fontWeight={600}>{t('season.endDate')}:</Box>
                                                                        <CalendarInput
                                                                            format='dd.MM.yyyy'
                                                                            value={new Date(edited.endDate)}
                                                                            onChange={(date) => {
                                                                                if (date && !isNaN(+date)) {
                                                                                    handleChange(req.id, 'endDate', date.toISOString());
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <Box fontWeight={600}>{t('employees.agreements.field.expirationDate')}:</Box>
                                                                        <CalendarInput
                                                                            format='dd.MM.yyyy'
                                                                            value={edited.expiration ? new Date(edited.expiration) : null}
                                                                            onChange={(date) => {
                                                                                if (date && !isNaN(+date)) {
                                                                                    handleChange(req.id, 'expiration', date.toISOString());
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={8}>
                                                                        <Box fontWeight={600}>{t('status.label')}:</Box>
                                                                        <Switch
                                                                            checked={!edited.closed}
                                                                            onChange={(e) => { handleChange(req.id, 'closed', !e.target.checked) }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={4} textAlign="right">
                                                                        <IconButton onClick={() => { edited && handleSave(edited) }}>
                                                                            <CheckIcon />
                                                                        </IconButton>
                                                                    </Grid>
                                                                </>
                                                            )}
                                                        </Grid>
                                                    </StyledAccordionDetails>
                                                </StyledAccordion>
                                            );
                                        })}
                                    </StyledAccordionDetails>
                                </StyledAccordion>
                            </Grid>
                        )}
                        <Grid item xs={12} />
                        <Grid item xs={6}>
                            <Button
                                fullWidth
                                size='small'
                                variant='outlined'
                                onClick={onClose}
                            >
                                {t('common.forms.button.cancel')}
                            </Button>
                        </Grid>
                        <Grid item xs={6}>
                            <Button fullWidth size='small' type="submit" variant='contained'>
                                {t('request')}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </ModalContentWrapper>
        </StyledAppModal>
    );
};

export default RequestAviability;
